import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

const useAiProposal = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest, isLoading, error, setError } = useHttp();

  // Loads a single proposal including the heavy fields (action.body, currentStateSnapshot)
  const getAiProposalById = useCallback(
    async aiProposalId => {
      try {
        return await sendRequest({
          url: `/api/v1/aiProposal/${aiProposalId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden des KI-Vorschlags');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Loads aggregated proposal counts (total, by status, by target domain)
  const getAiProposalStats = useCallback(async () => {
    try {
      return await sendRequest({
        url: '/api/v1/aiProposal/stats',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    } catch (err) {
      toast.error('Fehler beim Laden der KI-Vorschlags-Statistik');
      throw err;
    }
  }, [authCtx, sendRequest]);

  // Approves a proposal; the backend applies the proposed change immediately.
  // The note is optional. Returns null when the request was stored for step-up auth.
  const approveAiProposal = useCallback(
    async (aiProposalId, note) => {
      try {
        return await sendRequest({
          url: `/api/v1/aiProposal/${aiProposalId}/approve`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: note && note.trim() ? { note: note.trim() } : {},
        });
      } catch (err) {
        toast.error('Fehler beim Annehmen des KI-Vorschlags');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Rejects a proposal; the backend requires a non-empty note as rejection reason.
  const rejectAiProposal = useCallback(
    async (aiProposalId, note) => {
      try {
        return await sendRequest({
          url: `/api/v1/aiProposal/${aiProposalId}/reject`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { note },
        });
      } catch (err) {
        toast.error('Fehler beim Ablehnen des KI-Vorschlags');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  return {
    isLoading,
    error,
    setError,
    getAiProposalById,
    getAiProposalStats,
    approveAiProposal,
    rejectAiProposal,
  };
};

export default useAiProposal;
